// "Pakete gösterge ekle" › AI ile öner: isteğe bağlı not + Öner, yükleniyor/iptal, gerekçeli öneri
// ağacı ("önerildi" + tek cümle gerekçe — düz metin, React kaçışıyla), yeniden öner. Yüzeyde
// maliyet/token/süre yoktur. Durum `usePackageSuggest` ile pencere kapanana dek korunur (sekme
// değişince kaybolmaz); koşum sonucu Seçim'e pencere düzeyinde (`PackageAddDialog.suggestResult`)
// bir kez uygulanır — sekme o an açık olmasa da.

import { useEffect, useId, useMemo, useReducer, useRef, useState } from "react";
import { KPI_PACKAGE_SUGGEST_NOTE_MAX, type KpiMetric } from "@mahir/shared";
import { suggestKpiPackage } from "../../api/kpi-packages";
import {
  SUGGEST_IDLE,
  createSuggestRunner,
  mergeSuggestSelection,
  suggestErrorText,
  suggestReducer,
  suggestTreeMetrics,
  visibleSuggestions,
  type SuggestRunner,
  type SuggestState,
} from "../../lib/kpi-package-suggest";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import type { PackageSuggestApi } from "./PackageAddDialog";
import { CATALOG_TREE_KEYS, CatalogCheckboxTree } from "./CatalogCheckboxTree";
import { footerBtn, helpText, inputStyle, labelStyle, pillStyle } from "./kpi-form-styles";

const S = KPI_PACKAGES_COPY.add.suggest;

export interface PackageSuggestController {
  state: SuggestState;
  note: string;
  setNote: (v: string) => void;
  run: () => void;
  reset: () => void;
}

/** Düzenleyici düzeyinde tutulur: `active` false olunca (pencere kapandı) iptal + sıfırlama. */
export function usePackageSuggest(packageId: string, active: boolean): PackageSuggestController {
  const [state, dispatch] = useReducer(suggestReducer, SUGGEST_IDLE);
  const [note, setNote] = useState("");
  const runner = useRef<SuggestRunner | null>(null);
  runner.current ??= createSuggestRunner(suggestKpiPackage, dispatch);

  // Pencere kapanınca YA DA paket değişince (açık pencerede eski paketin önerisi kalmasın) sıfırla.
  const forPackage = useRef(packageId);
  useEffect(() => {
    if (active && forPackage.current === packageId) return;
    forPackage.current = packageId;
    runner.current?.dispose();
    dispatch({ type: "reset" });
    setNote("");
  }, [active, packageId]);
  useEffect(() => () => runner.current?.dispose(), []);

  return {
    state,
    note,
    setNote,
    run: () => runner.current?.run(packageId, note),
    reset: () => dispatch({ type: "reset" }),
  };
}

export interface PackageSuggestViewProps {
  state: SuggestState;
  note: string;
  onNote: (v: string) => void;
  onRun: () => void;
  metrics: readonly KpiMetric[];
  selected: ReadonlySet<string>;
  reasons: ReadonlyMap<string, string>;
  onChange: (next: Set<string>) => void;
  narrow?: boolean;
}

/** Sunumsal parça (SSR ile test edilir). */
export function PackageSuggestView(p: PackageSuggestViewProps): JSX.Element {
  const noteId = useId();
  const loading = p.state.kind === "loading";
  const done = p.state.kind === "done" ? p.state.res : null;
  const tree = useMemo(() => (done ? suggestTreeMetrics(p.metrics, done) : []), [p.metrics, done]);
  const visible = done ? visibleSuggestions(done) : [];
  return (
    <div className="grid gap-3" style={{ minWidth: 0 }}>
      <p style={helpText}>{S.intro}</p>
      <div>
        <label htmlFor={noteId} style={labelStyle}>
          {S.noteLabel}
        </label>
        <textarea
          id={noteId}
          value={p.note}
          rows={2}
          maxLength={KPI_PACKAGE_SUGGEST_NOTE_MAX}
          placeholder={S.notePlaceholder}
          disabled={loading}
          onChange={(e) => p.onNote(e.target.value)}
          style={{ ...inputStyle, width: "100%", resize: "vertical" }}
        />
        <div className="text-xs" style={{ color: "var(--color-text-2)", textAlign: "right" }}>
          {S.noteCount(p.note.length)}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" style={footerBtn(true)} disabled={loading} onClick={p.onRun}>
          {done || p.state.kind === "error" ? S.rerun : S.run}
        </button>
        {loading ? (
          <span role="status" className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {S.loading}
          </span>
        ) : null}
        {done && visible.length > 0 ? (
          <span className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {S.summary(visible.length, done.autoAdded.length, done.alreadyInPackage.length)}
          </span>
        ) : null}
      </div>
      {p.state.kind === "error" ? (
        <div
          role="alert"
          className="text-sm"
          style={{ color: "var(--color-danger-text, #b42318)" }}
        >
          {suggestErrorText(p.state.code)}
        </div>
      ) : null}
      {done && visible.length === 0 ? <p style={helpText}>{S.empty}</p> : null}
      {done && visible.length > 0 ? (
        <div
          className="rounded-lg"
          style={{ border: "1px solid var(--color-border)", overflow: "hidden", minWidth: 0 }}
        >
          <CatalogCheckboxTree
            collapseKey={CATALOG_TREE_KEYS.suggest}
            metrics={tree}
            selected={p.selected}
            search=""
            ariaLabel={S.treeLabel}
            maxHeight={380}
            alwaysScroll
            {...(p.narrow === undefined ? {} : { narrow: p.narrow })}
            onChange={p.onChange}
            rowContent={(m) => {
              const reason = p.reasons.get(m.id);
              if (reason === undefined) return null;
              return (
                <>
                  <span style={{ ...pillStyle, marginLeft: 6 }}>{S.suggestedPill}</span>
                  <span
                    className="text-xs"
                    title={reason}
                    style={{ color: "var(--color-text-2)", marginLeft: 6 }}
                  >
                    {reason}
                  </span>
                </>
              );
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

export interface PackageSuggestTabProps {
  controller: PackageSuggestController;
  metrics: readonly KpiMetric[];
  api: PackageSuggestApi;
  narrow?: boolean;
}

export function PackageSuggestTab(p: PackageSuggestTabProps): JSX.Element {
  const { controller: c, api } = p;
  const done = c.state.kind === "done" ? c.state.res : null;

  const reasons = useMemo(
    () => new Map((done?.suggestions ?? []).map((s) => [s.metricId, s.reason])),
    [done],
  );
  const selected = useMemo(() => new Set(api.picks.keys()), [api.picks]);

  function onChange(next: Set<string>): void {
    const shown = new Set(done ? suggestTreeMetrics(p.metrics, done).map((m) => m.id) : []);
    api.setPicks((prev) => mergeSuggestSelection(prev, shown, next, reasons));
  }

  return (
    <PackageSuggestView
      state={c.state}
      note={c.note}
      onNote={c.setNote}
      onRun={c.run}
      metrics={p.metrics}
      selected={selected}
      reasons={reasons}
      onChange={onChange}
      {...(p.narrow === undefined ? {} : { narrow: p.narrow })}
    />
  );
}
